import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import useApiFetch, {apiEndpoint} from '@tracker/shared/src/Auth/Api';
import InlineLoadingIndicator from '@tracker/shared/src/LoadingIndicator/InlineLoadingIndicator';
import React, {useEffect, useState} from 'react';

const useStyles = makeStyles(theme => ({
    tableHead: {
        backgroundColor: theme.custom.tableHeader,
        '& th': {
            color: theme.palette.text.primary,
            fontWeight: 500,
            lineHeight: 1.43,
        },
    },
    rowHead: {
        backgroundColor: theme.custom.tableHeader,
        color: theme.palette.text.primary,
        fontWeight: 500,
    },
}));

type Summary = {
    period : {
        start : string;
        end : string;
    };
    goal : {
        period : number;
        year : number;
    };
    total : {
        billable : number;
        project : number;
        tracked : number;
    };
    averagePerWeek : {
        billable : number;
        project : number;
        tracked : number;
    };
};

const dateFormatter = new Intl.DateTimeFormat();

const Utilization : React.FC = () => {
    const classes = useStyles();
    const apiFetch = useApiFetch();
    const [summary, setSummary] = useState<Summary | null>(null)

    useEffect(() => {
        (async () => {
            const response = await apiFetch(new URL('/v1/dashboard', apiEndpoint).toString());
            const summary = await response.json() as Summary;
            setSummary(summary);
        })();
    }, [apiFetch]);

    if (summary === null) {
        return <InlineLoadingIndicator/>;
    }

    const percentOfTarget = summary.total.project / summary.goal.period * 100;

    return (
        <>
            <Typography variant="h5" gutterBottom>
                Utilization from {dateFormatter.format(new Date(summary.period.start))}
                {' '}to {dateFormatter.format(new Date(summary.period.end))}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Year goal</TableCell>
                                    <TableCell>{Number(summary.goal.year.toFixed(2))}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Target to date</TableCell>
                                    <TableCell>{Number(summary.goal.period.toFixed(2))}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Project hours to date</TableCell>
                                    <TableCell>{summary.total.project.toFixed(2)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Percent of target</TableCell>
                                    <TableCell>{percentOfTarget.toFixed(0)}%</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Total</TableCell>
                                    <TableCell>Avg/Week</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Tracked</TableCell>
                                    <TableCell>{summary.total.tracked.toFixed(2)}</TableCell>
                                    <TableCell>{summary.averagePerWeek.tracked.toFixed(2)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Project</TableCell>
                                    <TableCell>{summary.total.project.toFixed(2)}</TableCell>
                                    <TableCell>{summary.averagePerWeek.project.toFixed(2)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.rowHead}>Billable</TableCell>
                                    <TableCell>{summary.total.billable.toFixed(2)}</TableCell>
                                    <TableCell>{summary.averagePerWeek.billable.toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </>
    );
};

export default Utilization;
